import React, { useState, useRef, Children } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Sprout, Building2, Package, ArrowRight, Linkedin, ExternalLink, X } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { MyceliumText, PulsingNode, RevealWrapper, organicEase } from '../components/MyceliumAnimations';
const portfolioData = {
  grow: {
    title: 'GROW',
    subtitle: 'AGRICULTURE',
    allocation: '40%',
    color: '#2BC7B7',
    icon: Sprout,
    companies: [{
      id: 'bopa',
      name: 'BOPA',
      logo: 'https://cdn.magicpatterns.com/uploads/p75Pt4T4irpKShWjQq5kQt/BOPA_Logos_Primary_2color-SlatePink.png',
      headshot: 'https://cdn.magicpatterns.com/uploads/j5YeKjhxpiTiQqzWAJbVtt/Sarah_Headshot.jpg',
      founder: 'Sarah Hinkley',
      title: 'Founder & CEO',
      tagline: 'Autonomous nano-tractors for Regenerative Agriculture',
      stage: 'Seed',
      stageIcon: '🌿',
      opportunity: '$163B',
      traction: '$5M in sales pipeline',
      coInvestors: 'FirstMile Ventures',
      website: 'https://barnowlag.com/',
      linkedin: 'https://www.linkedin.com/company/bopa-precision-ag/',
      angle: 0,
      distance: 220
    }, {
      id: 'tierrasphere',
      name: 'TierraSphere',
      logo: 'https://cdn.magicpatterns.com/uploads/98gSPHpRjHPGHmXTjPPktJ/TierraSphere-Logo_(1).png',
      headshot: 'https://cdn.magicpatterns.com/uploads/9L48deLNJpBFgHmaVDUwzx/Marcela_Headshot.jpg',
      founder: 'Marcela Flores',
      title: 'Founder & CEO',
      tagline: 'AI For Soil Regeneration',
      stage: 'Pre-Seed',
      stageIcon: '🌱',
      opportunity: '$10B',
      traction: '30+ brands in pipeline, including PepsiCo',
      coInvestors: 'Carbon13 Backed',
      website: 'https://www.tierrasphere.com/',
      linkedin: 'https://www.linkedin.com/company/tierrasphere/',
      angle: -60,
      distance: 160
    }]
  },
  build: {
    title: 'BUILD',
    subtitle: 'BUILT ENVIRONMENT',
    allocation: '40%',
    color: '#4DE7D4',
    icon: Building2,
    companies: [{
      id: 'reefcycle',
      name: 'ReefCycle',
      logo: 'https://cdn.magicpatterns.com/uploads/38zm79CuEt3kRT2MvHZqxo/reefcycle-logo-black.png',
      headshots: ['https://cdn.magicpatterns.com/uploads/4hhMPidW6aSsMkoc11grzo/Mary_Headshot.jpg', 'https://cdn.magicpatterns.com/uploads/dDkjGRK46uLePih7U1qyMZ/Shannon_Headshot.jpg'],
      founders: ['Mary Lempres', 'Shannon Parker'],
      titles: ['Founder & CEO', 'Co-founder'],
      tagline: 'Nature-based cement',
      stage: 'Pre-Seed',
      stageIcon: '🌱',
      opportunity: '$36B',
      traction: 'NCSU testing complete, 2nd patent pending',
      coInvestors: 'Vitality is the first VC',
      website: 'https://reefcycle.earth/',
      linkedin: 'https://www.linkedin.com/company/reefcycle/posts/?feedView=all',
      angle: 90,
      distance: 170
    }]
  },
  use: {
    title: 'USE',
    subtitle: 'CONSUMER PRODUCTS',
    allocation: '20%',
    color: '#D4C5A9',
    icon: Package,
    companies: [{
      id: 'mothership',
      name: 'Mothership Materials',
      logo: 'https://cdn.magicpatterns.com/uploads/jjDdoGf7sEwKjjTyW7U3fB/MM_logo.webp',
      headshot: 'https://cdn.magicpatterns.com/uploads/mZEUZGCYnhLX4sU1CuLncJ/Jo_Headshot.jpg',
      founder: 'Jo Marini',
      title: 'Co-founder & CEO',
      tagline: 'Containerized biomanufacturing',
      stage: 'Seed',
      stageIcon: '🌿',
      opportunity: '$360B',
      traction: '3-year pilot with AB InBev',
      coInvestors: 'Refashiond Ventures',
      website: 'https://www.mothershipmaterials.com/',
      linkedin: 'https://www.linkedin.com/company/mothershipmaterials/',
      angle: 190,
      distance: 210
    }]
  }
};
export function PortfolioRadialGarden() {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [hoveredSector, setHoveredSector] = useState<string | null>(null);
  const [highlightedCard, setHighlightedCard] = useState<string | null>(null);
  const gardenRef = useRef(null);
  const cardsRef = useRef<{
    [key: string]: HTMLDivElement | null;
  }>({});
  const handleNodeClick = (companyId: string) => {
    setHighlightedCard(companyId);
    const cardElement = cardsRef.current[companyId];
    if (cardElement) {
      cardElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };
  return <div className="min-h-screen bg-deepCanopy" data-id="element-1390">
      {/* Hero */}
      <section aria-label="Vitality portfolio of nature-inspired startups" className="relative min-h-[70vh] flex items-center justify-center overflow-hidden" data-id="element-1391">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D2B24] via-[#1F3A33] to-[#0D2B24]" data-id="element-1392" />

        {/* Subtle topographic lines */}
        <svg className="absolute inset-0 w-full h-full opacity-5" data-id="element-1393">
          {[...Array(8)].map((_, i) => <ellipse key={i} cx="50%" cy="50%" rx={`${20 + i * 10}%`} ry={`${15 + i * 8}%`} fill="none" stroke="#2BC7B7" strokeWidth="1" data-id="element-1394" />)}
        </svg>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1395">
          <motion.h1 className="text-6xl md:text-8xl font-bold text-white mb-8" initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.8
        }} data-id="element-1396">
            <MyceliumText as="span" delay={0.2} staggerChildren={0.05} data-id="element-1397">
              The Vitality Portfolio
            </MyceliumText>
          </motion.h1>

          <RevealWrapper delay={0.4} data-id="element-1398">
            <p className="text-2xl text-vitalityTeal mb-6 font-light" data-id="element-1399">
              Where Nature-Inspired Innovation Takes Root
            </p>
          </RevealWrapper>

          <RevealWrapper delay={0.5} data-id="element-1400">
            <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed" data-id="element-1401">
              Our portfolio grows from a single thesis: nature's 3.8 billion
              years of R&D holds the solutions to humanity's greatest
              challenges. These founders represent a seed of transformation,
              cultivated with intention and connected through shared purpose.
            </p>
          </RevealWrapper>
        </div>
      </section>

      {/* The Radial Garden Visualization */}
      <section ref={gardenRef} className="relative py-12 sm:py-20 bg-gradient-to-b from-[#0D2B24] to-[#1F3A33]" data-id="element-1402">
        <div className="max-w-7xl mx-auto px-4" data-id="element-1403">
          {/* Mobile hint */}
          <p className="text-center text-gray-500 text-sm mb-4 md:hidden" data-id="element-1404">
            Pinch to zoom • Tap companies to learn more
          </p>
          <div className="relative w-full max-w-5xl mx-auto overflow-x-auto md:overflow-visible" style={{
          aspectRatio: '1',
          minWidth: '320px'
        }} data-id="element-1405">
            <svg viewBox="0 0 800 800" className="w-full h-full" style={{
            filter: 'drop-shadow(0 0 40px rgba(43, 199, 183, 0.1))'
          }} data-id="element-1406">
              <defs data-id="element-1407">
                {/* Gradients for sectors */}
                <radialGradient id="growGradient" cx="50%" cy="50%" data-id="element-1408">
                  <stop offset="0%" stopColor="#2BC7B7" stopOpacity="0.2" data-id="element-1409" />
                  <stop offset="100%" stopColor="#2BC7B7" stopOpacity="0" data-id="element-1410" />
                </radialGradient>
                <radialGradient id="buildGradient" cx="50%" cy="50%" data-id="element-1411">
                  <stop offset="0%" stopColor="#4DE7D4" stopOpacity="0.2" data-id="element-1412" />
                  <stop offset="100%" stopColor="#4DE7D4" stopOpacity="0" data-id="element-1413" />
                </radialGradient>
                <radialGradient id="useGradient" cx="50%" cy="50%" data-id="element-1414">
                  <stop offset="0%" stopColor="#D4C5A9" stopOpacity="0.2" data-id="element-1415" />
                  <stop offset="100%" stopColor="#D4C5A9" stopOpacity="0" data-id="element-1416" />
                </radialGradient>

                {/* Glow filter */}
                <filter id="glow" data-id="element-1417">
                  <feGaussianBlur stdDeviation="4" result="coloredBlur" data-id="element-1418" />
                  <feMerge data-id="element-1419">
                    <feMergeNode in="coloredBlur" data-id="element-1420" />
                    <feMergeNode in="SourceGraphic" data-id="element-1421" />
                  </feMerge>
                </filter>
              </defs>

              {/* Garden bed background circle */}
              <motion.circle cx="400" cy="400" r="350" fill="#0D2B24" stroke="#2BC7B7" strokeWidth="2" strokeOpacity="0.2" initial={{
              scale: 0,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 1,
              ease: organicEase
            }} data-id="element-1422" />

              {/* Growth rings */}
              {[280, 220, 160, 100].map((r, i) => <motion.circle key={r} cx="400" cy="400" r={r} fill="none" stroke="#2BC7B7" strokeWidth="1" strokeOpacity="0.1" strokeDasharray="4 4" initial={{
              scale: 0,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              delay: 0.2 + i * 0.1,
              duration: 0.8
            }} data-id="element-1423" />)}

              {/* Three Sectors with organic edges */}
              {Object.entries(portfolioData).map(([key, theme], index) => {
              const startAngle = index * 120 - 90;
              const endAngle = startAngle + 120;
              const isHovered = hoveredSector === key;
              // Create organic sector path
              const createSectorPath = () => {
                const points: string[] = ['M 400 400'];
                const radius = 350;
                const steps = 30;
                for (let i = 0; i <= steps; i++) {
                  const angle = startAngle + 120 * i / steps;
                  const rad = angle * Math.PI / 180;
                  const wobble = Math.sin(i * 0.5) * 8;
                  const r = radius + wobble;
                  const x = 400 + r * Math.cos(rad);
                  const y = 400 + r * Math.sin(rad);
                  points.push(`${i === 0 ? 'L' : 'L'} ${x} ${y}`);
                }
                points.push('Z');
                return points.join(' ');
              };
              return <g key={key} data-id="element-1424">
                    <motion.path d={createSectorPath()} fill={`url(#${key}Gradient)`} stroke={theme.color} strokeWidth="2" strokeOpacity={isHovered ? 0.6 : 0.2} initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 0.5 + index * 0.1,
                  duration: 1
                }} onMouseEnter={() => setHoveredSector(key)} onMouseLeave={() => setHoveredSector(null)} style={{
                  cursor: 'pointer'
                }} data-id="element-1425" />

                    {/* Sector label */}
                    <motion.g initial={{
                  opacity: 0,
                  scale: 0
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  delay: 0.8 + index * 0.1,
                  duration: 0.6
                }} data-id="element-1426">
                      <text x={400 + 280 * Math.cos((startAngle + 60) * Math.PI / 180)} y={400 + 280 * Math.sin((startAngle + 60) * Math.PI / 180)} textAnchor="middle" fill={theme.color} fontSize="24" fontWeight="bold" data-id="element-1427">
                        {theme.title}
                      </text>
                      <text x={400 + 280 * Math.cos((startAngle + 60) * Math.PI / 180)} y={400 + 280 * Math.sin((startAngle + 60) * Math.PI / 180) + 20} textAnchor="middle" fill="#F3F5F1" fontSize="12" opacity="0.7" data-id="element-1428">
                        {theme.subtitle}
                      </text>
                      <text x={400 + 280 * Math.cos((startAngle + 60) * Math.PI / 180)} y={400 + 280 * Math.sin((startAngle + 60) * Math.PI / 180) + 36} textAnchor="middle" fill={theme.color} fontSize="11" fontWeight="600" data-id="element-1429">
                        {theme.allocation}
                      </text>
                    </motion.g>
                  </g>;
            })}

              {/* Root connections */}
              {Object.entries(portfolioData).map(([themeKey, theme]) => theme.companies.map((company, companyIndex) => {
              const angle = company.angle * Math.PI / 180;
              const x = 400 + company.distance * Math.cos(angle);
              const y = 400 + company.distance * Math.sin(angle);
              const isHovered = hoveredNode === company.id;
              // Create organic root path
              const controlX1 = 400 + company.distance * 0.6 * Math.cos(angle) + Math.random() * 20 - 10;
              const controlY1 = 400 + company.distance * 0.6 * Math.sin(angle) + Math.random() * 20 - 10;
              const controlX2 = 400 + company.distance * 0.3 * Math.cos(angle) + Math.random() * 20 - 10;
              const controlY2 = 400 + company.distance * 0.3 * Math.sin(angle) + Math.random() * 20 - 10;
              return <motion.g key={company.id} data-id="element-1430">
                      {/* Root path */}
                      <motion.path d={`M ${x} ${y} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, 400 400`} stroke={theme.color} strokeWidth={isHovered ? 3 : 2} strokeOpacity={isHovered ? 0.6 : 0.3} fill="none" strokeLinecap="round" initial={{
                  pathLength: 0,
                  opacity: 0
                }} animate={{
                  pathLength: 1,
                  opacity: 1
                }} transition={{
                  delay: 1.2 + companyIndex * 0.15,
                  duration: 1.5,
                  ease: organicEase
                }} data-id="element-1431" />

                      {/* Animated pulse along root */}
                      <motion.circle r="3" fill={theme.color} filter="url(#glow)" initial={{
                  opacity: 0
                }} animate={{
                  opacity: [0, 0.8, 0]
                }} transition={{
                  delay: 2 + companyIndex * 0.5,
                  duration: 3,
                  repeat: Infinity,
                  repeatDelay: 5
                }} data-id="element-1432">
                        <animateMotion dur="3s" repeatCount="indefinite" begin={`${2 + companyIndex * 0.5}s`} data-id="element-1433">
                          <mpath href={`#root-${company.id}`} xlinkHref={`#root-${company.id}`} data-id="element-1434" />
                        </animateMotion>
                      </motion.circle>

                      {/* Hidden path for animation */}
                      <path id={`root-${company.id}`} d={`M ${x} ${y} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, 400 400`} fill="none" opacity="0" data-id="element-1435" />
                    </motion.g>;
            }))}

              {/* Center Core - The Thesis */}
              <motion.g initial={{
              scale: 0,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              delay: 0.3,
              duration: 1,
              ease: organicEase
            }} data-id="element-1436">
                {/* Pulsing outer ring */}
                <motion.circle cx="400" cy="400" r="70" fill="none" stroke="#4DE7D4" strokeWidth="2" strokeOpacity="0.4" animate={{
                r: [70, 80, 70],
                strokeOpacity: [0.4, 0.1, 0.4]
              }} transition={{
                duration: 4,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-1437" />

                {/* Core orb */}
                <motion.circle cx="400" cy="400" r="60" fill="url(#coreGradient)" filter="url(#glow)" animate={{
                opacity: [0.8, 1, 0.8]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-1438" />

                <defs data-id="element-1439">
                  <radialGradient id="coreGradient" data-id="element-1440">
                    <stop offset="0%" stopColor="#4DE7D4" data-id="element-1441" />
                    <stop offset="100%" stopColor="#2BC7B7" data-id="element-1442" />
                  </radialGradient>
                </defs>

                {/* Core text */}
                <text x="400" y="400" textAnchor="middle" fill="#0D2B24" fontSize="12" fontWeight="700" data-id="element-1443">
                  VITALITY
                </text>
                <text x="400" y="415" textAnchor="middle" fill="#0D2B24" fontSize="11" fontWeight="600" data-id="element-1444">
                  THESIS
                </text>
              </motion.g>

              {/* Company Plant Nodes */}
              {Object.entries(portfolioData).map(([themeKey, theme]) => theme.companies.map((company, companyIndex) => {
              const angle = company.angle * Math.PI / 180;
              const x = 400 + company.distance * Math.cos(angle);
              const y = 400 + company.distance * Math.sin(angle);
              const isHovered = hoveredNode === company.id;
              const isSeed = company.stage === 'Seed';
              return <motion.g key={company.id} initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                delay: 1.5 + companyIndex * 0.2,
                duration: 0.8,
                ease: organicEase
              }} whileHover={{
                scale: 1.15
              }} onMouseEnter={() => setHoveredNode(company.id)} onMouseLeave={() => setHoveredNode(null)} onClick={() => handleNodeClick(company.id)} style={{
                cursor: 'pointer'
              }} data-id="element-1445">
                      {/* Bloom glow ring */}
                      <motion.circle cx={x} cy={y} r={isSeed ? 45 : 35} fill="none" stroke={theme.color} strokeWidth={isHovered ? 4 : 2} strokeOpacity={isHovered ? 0.8 : 0.4} animate={{
                  r: isSeed ? [45, 48, 45] : [35, 38, 35]
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} data-id="element-1446" />

                      {/* Bloom center (founder headshot instead of logo) */}
                      <circle cx={x} cy={y} r={isSeed ? 38 : 30} fill="#FFFFFF" data-id="element-1447" />

                      {/* Founder headshot */}
                      <foreignObject x={x - (isSeed ? 38 : 30)} y={y - (isSeed ? 38 : 30)} width={(isSeed ? 38 : 30) * 2} height={(isSeed ? 38 : 30) * 2} data-id="element-1448">
                        <div style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    display: 'flex'
                  }} data-id="element-1449">
                          {company.headshots ? <>
                              <img src={company.headshots[0]} alt="Founder 1" style={{
                        width: '50%',
                        height: '100%',
                        objectFit: 'cover'
                      }} data-id="element-1450" />
                              <img src={company.headshots[1]} alt="Founder 2" style={{
                        width: '50%',
                        height: '100%',
                        objectFit: 'cover'
                      }} data-id="element-1451" />
                            </> : <img src={company.headshot} alt={`${company.founder} headshot`} style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }} data-id="element-1452" />}
                        </div>
                      </foreignObject>

                      {/* Stage indicator (petals/leaves) */}
                      {isSeed ?
                // Fuller bloom for Seed stage
                <>
                          {[0, 72, 144, 216, 288].map((petalAngle, i) => {
                    const rad = (petalAngle + company.angle) * Math.PI / 180;
                    const px = x + 42 * Math.cos(rad);
                    const py = y + 42 * Math.sin(rad);
                    return <motion.circle key={i} cx={px} cy={py} r="6" fill={theme.color} opacity="0.6" initial={{
                      scale: 0
                    }} animate={{
                      scale: 1
                    }} transition={{
                      delay: 1.7 + companyIndex * 0.2 + i * 0.05
                    }} data-id="element-1453" />;
                  })}
                        </> :
                // Seedling leaves for Pre-Seed
                <>
                          <motion.ellipse cx={x - 25} cy={y - 10} rx="8" ry="12" fill={theme.color} opacity="0.5" initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} transition={{
                    delay: 1.7 + companyIndex * 0.2
                  }} data-id="element-1454" />
                          <motion.ellipse cx={x + 25} cy={y - 10} rx="8" ry="12" fill={theme.color} opacity="0.5" initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} transition={{
                    delay: 1.75 + companyIndex * 0.2
                  }} data-id="element-1455" />
                        </>}

                      {/* Company name label */}
                      <text x={x} y={y + (isSeed ? 60 : 50)} textAnchor="middle" fill="#F3F5F1" fontSize="13" fontWeight="600" data-id="element-1456">
                        {company.name}
                      </text>

                      {/* Founder name */}
                      <text x={x} y={y + (isSeed ? 75 : 65)} textAnchor="middle" fill={theme.color} fontSize="10" opacity="0.8" data-id="element-1457">
                        {company.founder || company.founders?.join(' & ')}
                      </text>

                      {/* Hover tooltip */}
                      {isHovered && <motion.g initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.2
                }} data-id="element-1458">
                          <rect x={x - 80} y={y - 120} width="160" height="60" rx="8" fill="#0A1111" opacity="0.95" stroke={theme.color} strokeWidth="2" data-id="element-1459" />
                          <text x={x} y={y - 95} textAnchor="middle" fill={theme.color} fontSize="11" fontWeight="600" data-id="element-1460">
                            {company.opportunity} opportunity
                          </text>
                          <text x={x} y={y - 75} textAnchor="middle" fill="#F3F5F1" fontSize="9" opacity="0.9" data-id="element-1461">
                            {company.traction.slice(0, 40)}
                            {company.traction.length > 40 ? '...' : ''}
                          </text>
                        </motion.g>}
                    </motion.g>;
            }))}
            </svg>
          </div>
        </div>
      </section>

      {/* Detailed Portfolio Cards - Botanical Specimen Style */}
      <section aria-label="Portfolio company details and growth metrics" className="relative py-32 bg-gradient-to-b from-[#1F3A33] to-[#0D2B24]" data-id="element-1462">
        <div className="max-w-7xl mx-auto px-4" data-id="element-1463">
          <RevealWrapper data-id="element-1464">
            <h2 className="text-4xl md:text-5xl font-bold text-white text-center mb-4" data-id="element-1465">
              <MyceliumText as="span" staggerChildren={0.04} data-id="element-1466">
                Portfolio Deep Roots
              </MyceliumText>
            </h2>
          </RevealWrapper>

          <RevealWrapper delay={0.2} data-id="element-1467">
            <p className="text-vitalityTeal text-center mb-16" data-id="element-1468">
              Pre-Fund 1 Investments
            </p>
          </RevealWrapper>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-1469">
            {Object.entries(portfolioData).map(([themeKey, theme]) => theme.companies.map((company, index) => <motion.div key={company.id} ref={el => cardsRef.current[company.id] = el} className={`relative p-8 rounded-2xl bg-[#F3F5F1] border-4 transition-all duration-300 ${highlightedCard === company.id ? 'ring-4 ring-vitalityTeal shadow-2xl' : 'shadow-lg'}`} style={{
            borderColor: theme.color,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="100" height="100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M50 10 Q 30 30 50 50 T 50 90" stroke="%23D4C5A9" stroke-width="0.5" fill="none" opacity="0.1"/%3E%3C/svg%3E")'
          }} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.6
          }} whileHover={{
            y: -8,
            boxShadow: `0 20px 40px ${theme.color}40`
          }} data-id="element-1470">
                  {/* Theme bar */}
                  <div className="absolute top-0 left-0 right-0 h-2 rounded-t-2xl" style={{
              backgroundColor: theme.color
            }} data-id="element-1471" />

                  {/* Header */}
                  <div className="flex items-center gap-2 mb-4" data-id="element-1472">
                    <span className="px-3 py-1 rounded-full text-xs font-bold uppercase" style={{
                backgroundColor: `${theme.color}20`,
                color: theme.color
              }} data-id="element-1473">
                      {theme.title} • {theme.subtitle}
                    </span>
                  </div>

                  {/* Company Info */}
                  <div className="flex items-start gap-6 mb-6" data-id="element-1474">
                    {company.headshots ?
              // Multiple founders - show both headshots
              <div className="flex -space-x-4" data-id="element-1475">
                        {company.headshots.map((headshot: string, idx: number) => <div key={idx} className="w-20 h-20 rounded-full border-4 flex-shrink-0 overflow-hidden" style={{
                  backgroundColor: `${theme.color}15`,
                  borderColor: theme.color
                }} data-id="element-1476">
                              <img src={headshot} alt={`${company.founders?.[idx]} headshot`} className="w-full h-full object-cover" data-id="element-1477" />
                            </div>)}
                      </div> :
              // Single founder
              <div className="w-20 h-20 rounded-full border-4 flex-shrink-0 overflow-hidden" style={{
                backgroundColor: `${theme.color}15`,
                borderColor: theme.color
              }} data-id="element-1478">
                        <img src={company.headshot} alt={`${company.founder} headshot`} className="w-full h-full object-cover" data-id="element-1479" />
                      </div>}
                    <div className="flex-1" data-id="element-1480">
                      <div className="mb-3" data-id="element-1481">
                        <img src={company.logo} alt={`${company.name} logo`} className="h-8 object-contain" style={{
                    maxWidth: '200px'
                  }} data-id="element-1482" />
                      </div>
                      <p className="text-gray-600 text-sm italic mb-2" data-id="element-1483">
                        {company.tagline}
                      </p>
                      <p className="font-semibold" style={{
                  color: theme.color
                }} data-id="element-1484">
                        {company.founder || company.founders?.join(' & ')}
                      </p>
                      <p className="text-gray-500 text-xs" data-id="element-1485">
                        {company.title || company.titles?.join(' | ')}
                      </p>
                    </div>
                  </div>

                  {/* Growth Metrics - Styled like plant care instructions */}
                  <div className="bg-white/60 rounded-xl p-6 mb-4" data-id="element-1486">
                    <p className="text-xs font-bold text-gray-500 uppercase mb-4 tracking-wider" data-id="element-1487">
                      Growth Metrics
                    </p>
                    <div className="space-y-3" data-id="element-1488">
                      <div className="flex items-center justify-between" data-id="element-1489">
                        <span className="text-gray-600 text-sm flex items-center gap-2" data-id="element-1490">
                          <span data-id="element-1491">🌱</span> STAGE
                        </span>
                        <span className="font-semibold" style={{
                    color: theme.color
                  }} data-id="element-1492">
                          {company.stage}
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-1493">
                        <span className="text-gray-600 text-sm flex items-center gap-2" data-id="element-1494">
                          <span data-id="element-1495">🌍</span> OPPORTUNITY
                        </span>
                        <span className="font-semibold" style={{
                    color: theme.color
                  }} data-id="element-1496">
                          {company.opportunity}
                        </span>
                      </div>
                      <div className="flex items-start justify-between" data-id="element-1497">
                        <span className="text-gray-600 text-sm flex items-center gap-2" data-id="element-1498">
                          <span data-id="element-1499">📈</span> TRACTION
                        </span>
                        <span className="font-semibold text-right max-w-[60%] text-sm" style={{
                    color: theme.color
                  }} data-id="element-1500">
                          {company.traction}
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-1501">
                        <span className="text-gray-600 text-sm flex items-center gap-2" data-id="element-1502">
                          <span data-id="element-1503">🤝</span> CO-INVESTORS
                        </span>
                        <span className="font-semibold text-right max-w-[60%] text-sm" style={{
                    color: theme.color
                  }} data-id="element-1504">
                          {company.coInvestors}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Pressed leaf watermark */}
                  <div className="absolute bottom-4 right-4 opacity-5 pointer-events-none" style={{
              fontSize: '80px'
            }} data-id="element-1505">
                    🍃
                  </div>

                  {/* Links */}
                  <div className="flex items-center gap-3 mt-4" data-id="element-1506">
                    {company.website && <a href={company.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px]" style={{
                backgroundColor: `${theme.color}15`,
                color: theme.color
              }} onClick={e => e.stopPropagation()} data-id="element-1507">
                        <ExternalLink className="w-4 h-4" data-id="element-1508" />
                        Visit Website
                      </a>}
                    {company.linkedin && <a href={company.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-11 h-11 rounded-lg transition-colors" style={{
                backgroundColor: `${theme.color}15`,
                color: theme.color
              }} onClick={e => e.stopPropagation()} data-id="element-1509">
                        <Linkedin className="w-4 h-4" data-id="element-1510" />
                      </a>}
                  </div>
                </motion.div>))}
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="relative py-8 bg-[#0D2B24]" data-id="element-1511">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1512">
          <p className="text-sm text-gray-500" data-id="element-1513">
            Successful pre-fund investments are anticipated to be transferred to
            Fund 1 at cost, subject to their continued success and alignment
            with Fund 1's investment thesis
          </p>
        </div>
      </section>

      {/* Contact CTA - Plant a Seed */}
      <section className="relative py-32 bg-gradient-to-b from-[#0D2B24] to-[#0A1111]" data-id="element-1514">
        {/* Subtle root tendrils at edges */}
        <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none" data-id="element-1515">
          <path d="M 0 100 Q 100 80, 200 100 T 400 100" stroke="#2BC7B7" strokeWidth="2" fill="none" data-id="element-1516" />
          <path d="M 800 100 Q 700 120, 600 100 T 400 100" stroke="#2BC7B7" strokeWidth="2" fill="none" data-id="element-1517" />
        </svg>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1518">
          <RevealWrapper data-id="element-1519">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-1520">
              <MyceliumText as="span" staggerChildren={0.04} data-id="element-1521">
                Plant a Seed With Us
              </MyceliumText>
            </h2>
          </RevealWrapper>

          <RevealWrapper delay={0.2} data-id="element-1522">
            <p className="text-xl text-gray-300 mb-12" data-id="element-1523">
              Whether you're a founder with a nature-inspired solution or an
              investor aligned with our thesis, we'd love to connect.
            </p>
          </RevealWrapper>

          <RevealWrapper delay={0.4} data-id="element-1524">
            <div className="flex flex-col items-center gap-6" data-id="element-1525">
              <motion.div className="w-24 h-24 rounded-full bg-vitalityTeal/20 border-4 border-vitalityTeal flex items-center justify-center overflow-hidden" animate={{
              boxShadow: ['0 0 20px rgba(43, 199, 183, 0.3)', '0 0 40px rgba(43, 199, 183, 0.6)', '0 0 20px rgba(43, 199, 183, 0.3)']
            }} transition={{
              duration: 3,
              repeat: Infinity
            }} data-id="element-1526">
                <img src="https://cdn.magicpatterns.com/uploads/nPrbWRd99BLA7hL4gLSpQo/7_Natalya.jpg" alt="Natalya Wallin" className="w-full h-full object-cover" data-id="element-1527" />
              </motion.div>
              <div data-id="element-1528">
                <p className="text-2xl font-bold text-white" data-id="element-1529">Natalya Wallin</p>
                <p className="text-vitalityTeal" data-id="element-1530">Founding Partner & GP</p>
                <a href="mailto:natalya@vitalityvf.com" className="text-gray-400 hover:text-vitalityTeal transition-colors" data-id="element-1531">
                  natalya@vitalityvf.com
                </a>
                <div className="flex items-center justify-center gap-4 mt-2" data-id="element-1532">
                  <a href="https://www.linkedin.com/company/vitality-vf/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-vitalityTeal transition-colors" data-id="element-1533">
                    <Linkedin className="w-5 h-5" data-id="element-1534" />
                  </a>
                </div>
              </div>
              <motion.div whileHover={{
              scale: 1.05,
              boxShadow: '0 0 30px rgba(43, 199, 183, 0.5)'
            }} whileTap={{
              scale: 0.95
            }} data-id="element-1535">
                <Button variant="primary" className="rounded-full" data-id="element-1536">
                  Get in Touch
                  <ArrowRight className="w-4 h-4 ml-2" data-id="element-1537" />
                </Button>
              </motion.div>
            </div>
          </RevealWrapper>
        </div>
      </section>
    </div>;
}